import { useEffect, useRef } from 'react';
import pick from 'lodash/pick';

// https://wicg.github.io/visual-viewport/#the-visualviewport-interface
export type VisualViewport = {
  offsetLeft: number;
  offsetTop: number;
  pageLeft: number;
  pageTop: number;
  width: number;
  height: number;
  scale: number;
};

const getCallbackValue = (hasSupport, e?) => {
  if (!hasSupport) {
    return {
      offsetLeft: 0,
      offsetTop: 0,
      height: document.documentElement.clientHeight,
      width: document.documentElement.clientWidth,
      scale: 1,
      pageLeft: 0,
      pageTop: 0,
    };
  }

  if (e) {
    // We don't send the object back because in IOS the reference became always equal
    // and then clientcode would not update like they expected
    return pick(e.target, [
      'offsetLeft',
      'offsetTop',
      'pageLeft',
      'pageTop',
      'width',
      'height',
      'scale',
    ]);
  }

  const viewport = window.visualViewport;
  if (!viewport) {
    return getCallbackValue(false);
  }
  return viewport;
};

export function useVisualViewport(
  onViewportUpdate: (viewport: VisualViewport) => void,
  enabled = true
) {
  const pendingUpdate = useRef<boolean>(false);
  const supportsVisualViewport =
    typeof window !== 'undefined' && typeof window.visualViewport !== 'undefined';

  useEffect(() => {
    if (supportsVisualViewport && enabled) {
      const viewport = window.visualViewport;
      if (viewport) {
        onViewportUpdate(viewport);
      }
    }
  }, [onViewportUpdate, supportsVisualViewport, enabled]);

  // The real api
  useEffect(() => {
    if (!supportsVisualViewport || !enabled) {
      return;
    }

    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }

    const viewportHandler = (e: Event) => {
      if (pendingUpdate.current) {
        return;
      }
      pendingUpdate.current = true;

      requestAnimationFrame(() => {
        pendingUpdate.current = false;
        onViewportUpdate(getCallbackValue(supportsVisualViewport, e));
      });
    };

    viewport.addEventListener('scroll', viewportHandler);
    viewport.addEventListener('resize', viewportHandler);

    return () => {
      viewport.removeEventListener('scroll', viewportHandler);
      viewport.removeEventListener('resize', viewportHandler);
    };
  }, [pendingUpdate, onViewportUpdate, supportsVisualViewport, enabled]);

  // Fake it for older browsers, not a full polyfill because it doesn't deal with
  // scale or the offsets
  useEffect(() => {
    if (supportsVisualViewport || !enabled) {
      return;
    }

    onViewportUpdate(getCallbackValue(supportsVisualViewport));

    const viewportHandler = e => {
      if (pendingUpdate.current) {
        return;
      }
      pendingUpdate.current = true;

      requestAnimationFrame(() => {
        pendingUpdate.current = false;
        onViewportUpdate(getCallbackValue(supportsVisualViewport));
      });
    };

    window.addEventListener('scroll', viewportHandler);
    window.addEventListener('resize', viewportHandler);

    return () => {
      window.removeEventListener('scroll', viewportHandler);
      window.removeEventListener('resize', viewportHandler);
    };
  }, [enabled, onViewportUpdate, supportsVisualViewport]);
}
